// src/pages/actividades/components/ActividadFormModal.tsx
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Textarea,
  Label,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Switch,
} from '@/components/ui';
import { useCrearActividad, useActualizarActividad, useTiposActividad, useModulos } from '@/hooks/queries/useActividades';
import { useClientes } from '@/hooks/queries/useClientes';
import type { Actividad, ActividadCreate, ActividadUpdate, PrioridadActividad, AreaActividad } from '@/types';

const PATRONES = [
  { value: 'DIARIO', label: 'Diario' },
  { value: 'SEMANAL', label: 'Semanal' },
  { value: 'QUINCENAL', label: 'Quincenal' },
  { value: 'MENSUAL', label: 'Mensual' },
];

const DIAS_SEMANA = [
  { value: '1', label: 'L' },
  { value: '2', label: 'M' },
  { value: '3', label: 'Mi' },
  { value: '4', label: 'J' },
  { value: '5', label: 'V' },
  { value: '6', label: 'S' },
  { value: '7', label: 'D' },
];

// Preprocess for optional number fields: empty string/null/undefined → undefined, else Number()
const optionalNumber = z.preprocess(
  (val) => (val === '' || val === undefined || val === null) ? undefined : Number(val),
  z.number().optional(),
);

const schema = z.object({
  titulo: z.string().min(1, 'El titulo es requerido').max(500),
  descripcion: z.string().optional(),
  tipo_actividad_id: z.string().optional(),
  area: z.enum(['TRABAJO', 'ADMINISTRATIVO', 'PERSONAL']),
  prioridad: z.enum(['AAA', 'AA', 'A', 'B', 'C']),
  cliente_id: z.string().optional(),
  modulo_id: z.string().optional(),
  fecha_agendada: z.string().optional(),
  hora_agendada: z.string().optional(),
  fecha_limite: z.string().optional(),
  duracion_estimada_min: optionalNumber,
  // Recurrencia
  es_recurrente: z.boolean().default(false),
  patron_recurrencia: z.string().optional(),
  dias_semana: z.string().optional(),
  dia_mes: z.preprocess(
    (val) => (val === '' || val === undefined || val === null) ? undefined : Number(val),
    z.number().min(1).max(31).optional(),
  ),
  hora_recurrencia: z.string().optional(),
  fecha_fin_recurrencia: z.string().optional(),
}).refine(
  (data) => {
    if (data.es_recurrente && !data.patron_recurrencia) return false;
    return true;
  },
  { message: 'Seleccione un patron de recurrencia', path: ['patron_recurrencia'] }
).refine(
  (data) => {
    if (data.es_recurrente && data.patron_recurrencia === 'SEMANAL' && !data.dias_semana) return false;
    return true;
  },
  { message: 'Seleccione al menos un dia', path: ['dias_semana'] }
).refine(
  (data) => {
    if (data.es_recurrente && data.patron_recurrencia === 'MENSUAL' && !data.dia_mes) return false;
    return true;
  },
  { message: 'Seleccione el dia del mes', path: ['dia_mes'] }
);

type FormData = z.infer<typeof schema>;

interface Props {
  open: boolean;
  onClose: () => void;
  actividad?: Actividad;
  defaultValues?: { fecha_agendada?: string; hora_agendada?: string };
}

const AREAS = [
  { value: 'TRABAJO', label: 'Work' },
  { value: 'ADMINISTRATIVO', label: 'Admin' },
  { value: 'PERSONAL', label: 'Personal' },
];

const PRIORIDADES = [
  { value: 'AAA', label: 'AAA - Maxima' },
  { value: 'AA', label: 'AA - Alta' },
  { value: 'A', label: 'A - Normal' },
  { value: 'B', label: 'B - Baja' },
  { value: 'C', label: 'C - Minima' },
];

export function ActividadFormModal({ open, onClose, actividad, defaultValues }: Props) {
  const isEditing = !!actividad;
  const crearMutation = useCrearActividad();
  const actualizarMutation = useActualizarActividad();
  const { data: clientesData } = useClientes({ page: 1, page_size: 100 });
  const { data: tipos } = useTiposActividad();
  const { data: modulos } = useModulos();

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      titulo: '',
      descripcion: '',
      tipo_actividad_id: '',
      area: 'TRABAJO',
      prioridad: 'A',
      cliente_id: '',
      modulo_id: '',
      fecha_agendada: '',
      hora_agendada: '',
      fecha_limite: '',
      duracion_estimada_min: undefined,
      es_recurrente: false,
      patron_recurrencia: '',
      dias_semana: '',
      dia_mes: undefined,
      hora_recurrencia: '',
      fecha_fin_recurrencia: '',
    },
  });

  const esRecurrente = form.watch('es_recurrente');
  const patronRecurrencia = form.watch('patron_recurrencia');

  useEffect(() => {
    if (actividad) {
      form.reset({
        titulo: actividad.titulo,
        descripcion: actividad.descripcion || '',
        tipo_actividad_id: actividad.tipo_actividad_id || '',
        area: actividad.area,
        prioridad: actividad.prioridad,
        cliente_id: actividad.cliente_id || '',
        modulo_id: actividad.modulo_id || '',
        fecha_agendada: actividad.fecha_agendada || '',
        hora_agendada: actividad.hora_agendada || '',
        fecha_limite: actividad.fecha_limite || '',
        duracion_estimada_min: actividad.duracion_estimada_min,
        es_recurrente: actividad.es_recurrente,
        patron_recurrencia: actividad.patron_recurrencia || '',
        dias_semana: actividad.dias_semana || '',
        dia_mes: actividad.dia_mes,
        hora_recurrencia: actividad.hora_recurrencia || '',
        fecha_fin_recurrencia: actividad.fecha_fin_recurrencia || '',
      });
    } else {
      form.reset({
        titulo: '',
        descripcion: '',
        tipo_actividad_id: '',
        area: 'TRABAJO',
        prioridad: 'A',
        cliente_id: '',
        modulo_id: '',
        fecha_agendada: defaultValues?.fecha_agendada || '',
        hora_agendada: defaultValues?.hora_agendada || '',
        fecha_limite: '',
        duracion_estimada_min: undefined,
        es_recurrente: false,
        patron_recurrencia: '',
        dias_semana: '',
        dia_mes: undefined,
        hora_recurrencia: '',
        fecha_fin_recurrencia: '',
      });
    }
  }, [actividad, form, defaultValues, open]);

  const onSubmit = async (data: FormData) => {
    try {
      // Clean recurrence fields if not recurrent
      const {
        patron_recurrencia, dias_semana, dia_mes,
        hora_recurrencia, fecha_fin_recurrencia,
        ...baseData
      } = data;

      // Zod-validated data is safe to cast — fields match ActividadCreate/ActividadUpdate
      const submitData: Record<string, unknown> = {
        ...baseData,
        tipo_actividad_id: data.tipo_actividad_id || undefined,
        cliente_id: data.cliente_id || undefined,
        modulo_id: data.modulo_id || undefined,
        fecha_agendada: data.fecha_agendada || undefined,
        hora_agendada: data.hora_agendada || undefined,
        fecha_limite: data.fecha_limite || undefined,
        duracion_estimada_min: data.duracion_estimada_min || undefined,
      };

      if (data.es_recurrente) {
        submitData.patron_recurrencia = patron_recurrencia || undefined;
        submitData.dias_semana = dias_semana || undefined;
        submitData.dia_mes = dia_mes || undefined;
        submitData.hora_recurrencia = hora_recurrencia || undefined;
        submitData.fecha_fin_recurrencia = fecha_fin_recurrencia || undefined;
      }

      if (isEditing) {
        await actualizarMutation.mutateAsync({ id: actividad.id, data: submitData as unknown as ActividadUpdate });
      } else {
        await crearMutation.mutateAsync(submitData as unknown as ActividadCreate);
      }
      form.reset();
      onClose();
    } catch (err) {
      // Mutation errors are handled by the hook (toast shown there).
      // Log unexpected errors from data preparation.
      console.error('[ActividadFormModal] Submit error:', err);
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const toggleDiaSemana = (dia: string) => {
    const current = form.watch('dias_semana') || '';
    const dias = current ? current.split(',') : [];
    const idx = dias.indexOf(dia);
    if (idx >= 0) {
      dias.splice(idx, 1);
    } else {
      dias.push(dia);
    }
    dias.sort((a, b) => parseInt(a) - parseInt(b));
    form.setValue('dias_semana', dias.join(','), { shouldValidate: true });
  };

  const isLoading = crearMutation.isPending || actualizarMutation.isPending;

  const onFormError = (errors: Record<string, unknown>) => {
    console.error('[ActividadFormModal] Validation errors:', errors);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Editar Actividad' : 'Nueva Actividad'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit, onFormError)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="titulo">
              Titulo <span className="text-red-500">*</span>
            </Label>
            <Input
              id="titulo"
              {...form.register('titulo')}
              placeholder="Describa la actividad..."
            />
            {form.formState.errors.titulo && (
              <p className="text-sm text-red-500">{form.formState.errors.titulo.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Tipo de Actividad</Label>
            <Select
              value={form.watch('tipo_actividad_id') || 'none'}
              onValueChange={(value) => {
                const tipoId = value === 'none' ? '' : value;
                form.setValue('tipo_actividad_id', tipoId);
                // Auto-suggest area from tipo_actividad.area_default
                if (tipoId && tipos) {
                  const tipo = tipos.find((t) => t.id === tipoId);
                  if (tipo?.area_default && form.watch('area') === 'TRABAJO') {
                    form.setValue('area', tipo.area_default);
                  }
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccione tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin tipo</SelectItem>
                {tipos?.map((tipo) => (
                  <SelectItem key={tipo.id} value={tipo.id}>
                    {tipo.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Area</Label>
              <Select
                value={form.watch('area')}
                onValueChange={(value) => form.setValue('area', value as AreaActividad)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AREAS.map((area) => (
                    <SelectItem key={area.value} value={area.value}>
                      {area.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Prioridad</Label>
              <Select
                value={form.watch('prioridad')}
                onValueChange={(value) => form.setValue('prioridad', value as PrioridadActividad)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORIDADES.map((prioridad) => (
                    <SelectItem key={prioridad.value} value={prioridad.value}>
                      {prioridad.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="cliente_id">Cliente (opcional)</Label>
            <Select
              value={form.watch('cliente_id') || 'none'}
              onValueChange={(value) => form.setValue('cliente_id', value === 'none' ? '' : value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccione un cliente" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin cliente</SelectItem>
                {clientesData?.items?.map((cliente) => (
                  <SelectItem key={cliente.id} value={cliente.id}>
                    {cliente.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Modulo</Label>
            <Select
              value={form.watch('modulo_id') || 'none'}
              onValueChange={(value) => form.setValue('modulo_id', value === 'none' ? '' : value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccione modulo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin modulo</SelectItem>
                {modulos && (() => {
                  const grupos = [...new Set(modulos.map((m) => m.grupo))];
                  return grupos.map((grupo) => (
                    <div key={grupo}>
                      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">{grupo}</div>
                      {modulos
                        .filter((m) => m.grupo === grupo)
                        .map((modulo) => (
                          <SelectItem key={modulo.id} value={modulo.id}>
                            {modulo.nombre}
                          </SelectItem>
                        ))}
                    </div>
                  ));
                })()}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="fecha_agendada">Fecha Agendada</Label>
              <Input id="fecha_agendada" type="date" {...form.register('fecha_agendada')} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="hora_agendada">Hora</Label>
              <Input id="hora_agendada" type="time" {...form.register('hora_agendada')} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="fecha_limite">Fecha Limite</Label>
              <Input id="fecha_limite" type="date" {...form.register('fecha_limite')} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="duracion_estimada_min">Duracion (min)</Label>
              <Input
                id="duracion_estimada_min"
                type="number"
                {...form.register('duracion_estimada_min')}
                placeholder="60"
              />
            </div>
          </div>

          {/* Recurrencia */}
          <div className="space-y-3 border rounded-lg p-3">
            <div className="flex items-center justify-between">
              <Label htmlFor="es_recurrente" className="text-sm font-medium">
                Actividad recurrente
              </Label>
              <Switch
                id="es_recurrente"
                checked={esRecurrente}
                onCheckedChange={(checked) => form.setValue('es_recurrente', checked)}
              />
            </div>

            {esRecurrente && (
              <div className="space-y-3 pt-2 border-t">
                <div className="space-y-2">
                  <Label>Patron</Label>
                  <Select
                    value={patronRecurrencia || 'none'}
                    onValueChange={(value) => form.setValue('patron_recurrencia', value === 'none' ? '' : value, { shouldValidate: true })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccione patron" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Seleccione...</SelectItem>
                      {PATRONES.map((p) => (
                        <SelectItem key={p.value} value={p.value}>
                          {p.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {form.formState.errors.patron_recurrencia && (
                    <p className="text-sm text-red-500">{form.formState.errors.patron_recurrencia.message}</p>
                  )}
                </div>

                {patronRecurrencia === 'SEMANAL' && (
                  <div className="space-y-2">
                    <Label>Dias de la semana</Label>
                    <div className="flex gap-1">
                      {DIAS_SEMANA.map((dia) => {
                        const selected = (form.watch('dias_semana') || '').split(',').includes(dia.value);
                        return (
                          <button
                            key={dia.value}
                            type="button"
                            onClick={() => toggleDiaSemana(dia.value)}
                            className={`w-9 h-9 rounded-md text-sm font-medium transition-colors ${
                              selected
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-muted text-muted-foreground hover:bg-muted/80'
                            }`}
                          >
                            {dia.label}
                          </button>
                        );
                      })}
                    </div>
                    {form.formState.errors.dias_semana && (
                      <p className="text-sm text-red-500">{form.formState.errors.dias_semana.message}</p>
                    )}
                  </div>
                )}

                {patronRecurrencia === 'MENSUAL' && (
                  <div className="space-y-2">
                    <Label htmlFor="dia_mes">Dia del mes</Label>
                    <Select
                      value={form.watch('dia_mes')?.toString() || 'none'}
                      onValueChange={(value) => form.setValue('dia_mes', value === 'none' ? undefined : parseInt(value), { shouldValidate: true })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccione dia" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Seleccione...</SelectItem>
                        {Array.from({ length: 31 }, (_, i) => i + 1).map((dia) => (
                          <SelectItem key={dia} value={dia.toString()}>
                            {dia}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {form.formState.errors.dia_mes && (
                      <p className="text-sm text-red-500">{form.formState.errors.dia_mes.message}</p>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="hora_recurrencia">Hora (opcional)</Label>
                    <Input id="hora_recurrencia" type="time" {...form.register('hora_recurrencia')} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="fecha_fin_recurrencia">Hasta (opcional)</Label>
                    <Input id="fecha_fin_recurrencia" type="date" {...form.register('fecha_fin_recurrencia')} />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="descripcion">Descripcion</Label>
            <Textarea
              id="descripcion"
              {...form.register('descripcion')}
              placeholder="Describa la actividad en detalle..."
              rows={4}
            />
          </div>

          {Object.keys(form.formState.errors).length > 0 && (
            <p className="text-sm text-red-500">
              Hay errores en el formulario. Revise los campos marcados.
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={handleClose}>
              Cancelar
            </Button>
            <Button type="submit" loading={isLoading}>
              {isEditing ? 'Guardar' : 'Crear Actividad'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
